import { useState, FormEvent } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function FinancingPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    yearsInBusiness: '',
    estimatedProjectCost: '',
    preferredTerm: '',
    serviceInterest: '',
    acreage: '',
    additionalInfo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d640v1r0723ekv2itjng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessName: '',
          businessType: '',
          yearsInBusiness: '',
          estimatedProjectCost: '',
          preferredTerm: '',
          serviceInterest: '',
          acreage: '',
          additionalInfo: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Agricultural Drone Service Financing",
    "description": "Flexible financing options for drone spraying and precision agriculture services. 0% APR promotional plans available with terms from 6-24 months.",
    "mainEntity": {
      "@type": "FinancialProduct",
      "name": "Agricultural Service Financing",
      "description": "Financing for drone spraying, crop imaging, and land management services",
      "provider": {
        "@type": "Organization",
        "name": "Greenfield Partners"
      }
    }
  };

  const financingOptions = [
    {
      term: '6 Months',
      apr: '0%',
      highlight: 'Promotional',
      description: 'Perfect for seasonal treatments',
      minPayment: 'As low as $167/mo on $1,000',
    },
    {
      term: '12 Months',
      apr: '0%',
      highlight: 'Most Popular',
      description: 'Balance payments with cash flow',
      minPayment: 'As low as $83/mo on $1,000',
    },
    {
      term: '18 Months',
      apr: 'Low Rate',
      highlight: 'Extended',
      description: 'Lower monthly payments',
      minPayment: 'As low as $58/mo on $1,000',
    },
    {
      term: '24 Months',
      apr: 'Low Rate',
      highlight: 'Maximum Flexibility',
      description: 'Spread costs over two seasons',
      minPayment: 'As low as $45/mo on $1,000',
    },
  ];

  const benefits = [
    {
      icon: 'ri-percent-line',
      title: '0% APR Promotional Plans',
      description: 'Qualified applicants can access interest-free financing on 6 and 12-month terms.',
    },
    {
      icon: 'ri-time-line',
      title: 'Quick Approval Process',
      description: 'Most applications receive a decision within 24 hours—often same day.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'No Money Down Options',
      description: 'Get started immediately without a large upfront investment.',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Payment Terms',
      description: 'Choose from 6, 12, 18, or 24-month terms to match your cash flow.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Simple Application',
      description: 'Minimal paperwork required. Apply online in minutes.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'No Prepayment Penalties',
      description: 'Pay off your balance early without any additional fees.',
    },
  ];

  const faqs = [
    {
      question: 'Who qualifies for 0% APR financing?',
      answer: 'Our 0% APR promotional plans are available to qualified applicants based on creditworthiness. Most established agricultural operations with good credit history qualify. We encourage you to apply—there\'s no impact to your credit score for checking eligibility.',
    },
    {
      question: 'What services can be financed?',
      answer: 'All Greenfield Partners services are eligible for financing, including precision aerial spraying, crop health imaging, NDVI mapping, timber assessment, deer deterrent programs, and comprehensive land management packages.',
    },
    {
      question: 'How long does approval take?',
      answer: 'Most applications receive a decision within 24 hours. Many applicants are approved the same day they apply, allowing you to schedule services immediately.',
    },
    {
      question: 'Is there a minimum or maximum financing amount?',
      answer: 'We offer financing for services starting at $500 up to $50,000. For larger projects or multi-year service agreements, contact us to discuss custom financing arrangements.',
    },
    {
      question: 'Can I pay off my balance early?',
      answer: 'Absolutely. There are no prepayment penalties. You can pay off your balance at any time without additional fees, and you\'ll only pay interest on the time you actually used the financing.',
    },
    {
      question: 'What information do I need to apply?',
      answer: 'You\'ll need basic contact information, business details (name, type, years in operation), estimated project cost, and preferred payment term. No financial statements are required for the initial application.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Agricultural Drone Service Financing | 0% APR Available | Greenfield Partners"
        description="Flexible financing for drone spraying and precision agriculture services. 0% APR promotional plans, 6-24 month terms, quick approval. Apply online today."
        keywords="agricultural financing, drone service financing, 0% APR farm financing, crop spraying payment plans, precision agriculture financing"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[550px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ffb09e0a7c4a22efe61cc7106e321d7c.jpg"
            alt="Prosperous agricultural farmland aerial view representing growth and investment"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-emerald-600/90 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-percent-line"></i>
              0% APR Promotional Plans Available
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Invest in Your Yield.<br />Pay Over Time.
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Flexible financing options make precision agriculture accessible. Protect your crops now and spread the cost over 6 to 24 months—with promotional 0% APR available for qualified applicants.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#apply"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-lg"
              >
                Apply Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="tel:+15551234567"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-white/30"
              >
                <i className="ri-phone-line"></i>
                Call to Discuss Options
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Why Finance Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-800 font-semibold tracking-wide mb-3">WHY FINANCE?</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-5">
              Smart Farmers Spread the Cost
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Precision drone services pay for themselves through better yields and reduced input costs. Financing lets you capture those benefits immediately while managing cash flow. <a href="/case-studies" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">See real examples <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg mb-4">
                  <i className={`${benefit.icon} text-2xl text-emerald-700`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Financing Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-800 font-semibold tracking-wide mb-3">FINANCING OPTIONS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-5">
              Choose Your Term
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select the payment schedule that works best for your operation. All options include no prepayment penalties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {financingOptions.map((option, index) => (
              <div
                key={index}
                className={`relative rounded-xl p-6 border-2 transition-all hover:shadow-lg ${
                  option.highlight === 'Most Popular'
                    ? 'border-emerald-600 bg-emerald-50/50'
                    : 'border-gray-200 bg-white hover:border-emerald-300'
                }`}
              >
                {option.highlight === 'Most Popular' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                {option.highlight === 'Promotional' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    Promotional
                  </div>
                )}
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 mb-1">{option.term}</p>
                  <div className="inline-flex items-center gap-1 text-emerald-700 font-semibold mb-3">
                    {option.apr === '0%' ? (
                      <>
                        <span className="text-2xl">0%</span>
                        <span className="text-sm">APR</span>
                      </>
                    ) : (
                      <span className="text-lg">{option.apr}</span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{option.description}</p>
                  <div className="bg-gray-100 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-500">{option.minPayment}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            * 0% APR available on 6 and 12-month terms for qualified applicants. Rates on 18 and 24-month terms vary based on creditworthiness. All financing subject to credit approval.
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* How It Works */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-800 font-semibold tracking-wide mb-3">HOW IT WORKS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-5">
              Simple 4-Step Process
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Apply Online', desc: 'Complete our simple application in under 5 minutes', icon: 'ri-file-edit-line' },
              { step: '02', title: 'Quick Review', desc: 'Receive a decision within 24 hours—often same day', icon: 'ri-search-line' },
              { step: '03', title: 'Schedule Service', desc: 'Once approved, book your drone services immediately', icon: 'ri-calendar-line' },
              { step: '04', title: 'Pay Over Time', desc: 'Make easy monthly payments that fit your budget', icon: 'ri-wallet-3-line' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative inline-block mb-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-700 rounded-full mx-auto">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center bg-amber-500 text-white text-sm font-bold rounded-full">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Application Form */}
      <section id="apply" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <p className="text-emerald-800 font-semibold tracking-wide mb-3">APPLY NOW</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">
                Start Your Application
              </h2>
              <p className="text-gray-600 mb-8">
                Complete the form below to check your eligibility. No impact to your credit score for the initial inquiry.
              </p>

              {submitStatus === 'success' ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-emerald-700"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                  <p className="text-gray-600 mb-4">
                    Thank you for your interest in financing. Our team will review your application and contact you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-emerald-700 font-medium hover:text-emerald-800 cursor-pointer"
                  >
                    Submit Another Application
                  </button>
                </div>
              ) : (
                <form
                  id="financing-application-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        placeholder="john@farm.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Business/Farm Name *</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      placeholder="Smith Family Farms"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Type *</label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                      >
                        <option value="">Select type...</option>
                        <option value="Row Crops">Row Crops</option>
                        <option value="Grain Production">Grain Production</option>
                        <option value="Livestock/Pasture">Livestock/Pasture</option>
                        <option value="Timber/Forestry">Timber/Forestry</option>
                        <option value="Specialty Crops">Specialty Crops</option>
                        <option value="Mixed Operation">Mixed Operation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Years in Business *</label>
                      <select
                        name="yearsInBusiness"
                        value={formData.yearsInBusiness}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="Less than 1 year">Less than 1 year</option>
                        <option value="1-3 years">1-3 years</option>
                        <option value="3-5 years">3-5 years</option>
                        <option value="5-10 years">5-10 years</option>
                        <option value="10+ years">10+ years</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Estimated Project Cost *</label>
                      <select
                        name="estimatedProjectCost"
                        value={formData.estimatedProjectCost}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                      >
                        <option value="">Select range...</option>
                        <option value="$500 - $1,000">$500 - $1,000</option>
                        <option value="$1,000 - $2,500">$1,000 - $2,500</option>
                        <option value="$2,500 - $5,000">$2,500 - $5,000</option>
                        <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                        <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                        <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                        <option value="$50,000+">$50,000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Term *</label>
                      <select
                        name="preferredTerm"
                        value={formData.preferredTerm}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                      >
                        <option value="">Select term...</option>
                        <option value="6 months (0% APR)">6 months (0% APR)</option>
                        <option value="12 months (0% APR)">12 months (0% APR)</option>
                        <option value="18 months">18 months</option>
                        <option value="24 months">24 months</option>
                        <option value="Not sure - need guidance">Not sure - need guidance</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Interest *</label>
                      <select
                        name="serviceInterest"
                        value={formData.serviceInterest}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                      >
                        <option value="">Select service...</option>
                        <option value="Precision Aerial Spraying">Precision Aerial Spraying</option>
                        <option value="Crop Health Imaging">Crop Health Imaging</option>
                        <option value="NDVI/Thermal Mapping">NDVI/Thermal Mapping</option>
                        <option value="Timber Assessment">Timber Assessment</option>
                        <option value="Deer Deterrent Program">Deer Deterrent Program</option>
                        <option value="Multiple Services">Multiple Services</option>
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Approximate Acreage</label>
                      <input
                        type="text"
                        name="acreage"
                        value={formData.acreage}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        placeholder="e.g., 500 acres"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Additional Information</label>
                    <textarea
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                      placeholder="Tell us about your operation or any questions you have..."
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">{formData.additionalInfo.length}/500 characters</p>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl"></i>
                      <p className="text-red-700 text-sm">Something went wrong. Please try again or call us directly.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-700 hover:bg-emerald-600 disabled:bg-emerald-400 text-white font-bold py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to be contacted regarding your financing inquiry. Your information is secure and will not be shared with third parties.
                  </p>
                </form>
              )}
            </div>

            {/* Side Info */}
            <div className="lg:pl-8">
              <div className="bg-emerald-800 rounded-xl p-8 text-white mb-8">
                <h3 className="text-xl font-bold mb-4">Prefer to Talk?</h3>
                <p className="text-emerald-100 mb-6">
                  Our team is happy to walk you through financing options and answer any questions.
                </p>
                <a
                  href="tel:+15551234567"
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 rounded-full">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Call Us</p>
                    <p className="text-emerald-200 text-sm">(555) 123-4567</p>
                  </div>
                </a>
              </div>

              <div className="bg-stone-50 rounded-xl p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">What to Expect</h3>
                <ul className="space-y-4">
                  {[
                    'No hard credit pull for initial inquiry',
                    'Decision typically within 24 hours',
                    'Dedicated financing specialist assigned',
                    'Flexible terms tailored to your operation',
                    'No hidden fees or surprises',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-emerald-100 rounded-full mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-emerald-700 text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 p-6 border border-amber-200 bg-amber-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-amber-600 text-xl mt-0.5"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Pro Tip</h4>
                    <p className="text-sm text-gray-600">
                      Many farmers find that their monthly savings from precision application exceed their monthly financing payment—making the service essentially free. <a href="/seasonal-programs" className="text-emerald-800 hover:text-emerald-700 font-semibold">Explore seasonal programs</a> for additional savings.
                    </p>
                    <a
                      href="/roi-calculator"
                      className="inline-flex items-center gap-1 text-emerald-700 font-medium text-sm mt-2 hover:text-emerald-800 cursor-pointer"
                    >
                      Calculate Your ROI
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-800 font-semibold tracking-wide mb-3">FREQUENTLY ASKED QUESTIONS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-5">
              Financing FAQs
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <i className="ri-add-line text-xl text-emerald-700 group-open:hidden"></i>
                  <i className="ri-subtract-line text-xl text-emerald-700 hidden group-open:block"></i>
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Final CTA */}
      <section className="py-20 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-5">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Don't let budget timing hold back your yield. Apply for financing today and protect your crops this season.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-8 py-4 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              Apply Now
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/instant-quote"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-emerald-500"
            >
              Get an Instant Quote First
            </a>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 bg-stone-100 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            <strong>Financing Disclosure:</strong> All financing is subject to credit approval and qualification requirements. 0% APR promotional financing is available on 6 and 12-month terms for qualified applicants. Rates on 18 and 24-month terms vary based on creditworthiness. Minimum financing amount is $500; maximum is $50,000. Contact us for complete terms and conditions. Greenfield Partners works with third-party financing partners to provide these services.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
